import React, { FormEvent, useCallback, useEffect, useState } from "react";

import classNames from "classnames";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { Tooltip } from "react-tooltip";

import {
  DndContext,
  DragEndEvent,
  MouseSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ExampleMedia, ExampleMediaType, ProductData } from "data/product/products";
import { StorefrontData } from "data/storefront";
import { Button, LargeInput } from "studio-design-system";
import { AnimatedSpinner } from "studio-design-system";
import { v4 } from "uuid";

import { CloudinaryTag, uploadFileToCloudinary } from "~/api/creatorApi";
import { ApiStatus } from "~/api/fetchStudioApi";
import { ArrowRightIcon } from "~/components/cart/Icons/ArrowRightIcon";
import { MusicNoteIcon } from "~/components/common/Icons/MusicNoteIcon";
import { SpotifyIcon } from "~/components/common/Icons/SpotifyIcon";
import { TikTokIcon } from "~/components/common/Icons/TikTokIcon";
import { TrashIcon, VideoDragHandle } from "~/components/common/Icons/VideoPreviewIcons";
import { YouTubeIcon } from "~/components/common/Icons/YouTubeIcon";
import { ImageWithBlur } from "~/components/common/ImageWithBlur/ImageWithBlur";
import UploadButton from "~/components/myClasses/UploadButton/UploadButton";
import UploadVideoState from "~/components/myClasses/uploadVideoState/UploadVideoState";
import { product } from "~/components/videoProductPage/Product.css";
import { useAuth } from "~/hooks/auth/useAuth";
import { useCreatorEditing } from "~/hooks/useCreatorEditing";
import {
  formatSpotifyLink,
  formatYouTubeLink,
  isSpotifyTrackLink,
  formatTikTokLink,
} from "~/util/stringUtils";
import { emailValidRegex } from "~/util/validateEmail";

import {
  sectionTitle,
  sectionWrap,
  topicsTitle,
  sectionBody,
  audioPlayerContainer,
  audioPlayerTitle,
  audioImageContainer,
  audioFlexContainer,
  audioImageBorderRadius,
} from "../VideoSection.css";
import * as styles from "./ApplyKnowledge.css";

const AudioPlayer = dynamic(
  () =>
    import(
      "~/components/videoProductPage/components/VideoSection/BeatPreview/AudioPlayer/AudioPlayer"
    ),
  { ssr: false },
);

interface ApplyKnowledgeData {
  subtitle: string;
  title: string;
  description: string;
  youtubeEmbed?: {
    url: string;
    title: string;
  }[];
  spotifyEmbed?: {
    url: string;
    title: string;
  };
  audioPlayer?: {
    url: string;
    title: string;
    imageUrl?: string;
  }[];
}

export interface AudioLocalData {
  name: string;
  duration: number;
  isLoading: boolean;
  progress: number;
}

export const AudioExampleMediaPlayer = ({
  index,
  mediaObject,
}: {
  index: number;
  mediaObject: ExampleMedia;
}) => {
  return (
    <div
      key={index}
      className={audioPlayerContainer}
      style={{ marginBottom: "0px", marginTop: "0px" }}
    >
      {mediaObject.mediaTitle && <p className={audioPlayerTitle}>{mediaObject.mediaTitle}</p>}
      <div className={audioFlexContainer}>
        {mediaObject.mediaThumbnailImage && (
          <div className={audioImageContainer}>
            <ImageWithBlur
              src={mediaObject.mediaThumbnailImage}
              width={72}
              height={72}
              objectFit="cover"
              className={audioImageBorderRadius}
            />
          </div>
        )}
        {mediaObject.mediaLink && (
          <AudioPlayer
            src={mediaObject.mediaLink}
            customStyles={{ width: "460px", waveContainerWidth: "295px" }}
          />
        )}
      </div>
    </div>
  );
};

export const SortableMediaItem = (props: {
  id: string;
  index: number;
  mediaObject: ExampleMedia;
  isInEditMode: boolean;
  handleDeleteItem: (itemIndex: number) => void;
}) => {
  const { mediaObject, index, isInEditMode } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.id,
    disabled: !props.isInEditMode,
  });
  if (transform) {
    transform.scaleY = 1;
  }
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      className={classNames(styles.mediaItem, isDragging && styles.dragging)}
      key={props.id}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      {isInEditMode && (
        <div className={styles.draghandle}>
          <div
            role="button"
            className={`cursor-pointer media_${index}_tooltip`}
            data-tooltip-content="Drag to move"
          >
            <VideoDragHandle />
          </div>
        </div>
      )}

      {mediaObject.mediaType == ExampleMediaType.YOUTUBE ? (
        <iframe
          key={index}
          width="100%"
          height="393px"
          src={formatYouTubeLink(mediaObject.mediaLink || "")}
          style={{ border: 0, borderRadius: 12 }}
          loading="lazy"
        />
      ) : null}

      {mediaObject.mediaType == ExampleMediaType.TIKTOK ? (
        <iframe
          key={index}
          width="100%"
          height="740px"
          src={formatTikTokLink(mediaObject.mediaLink || "")}
          style={{ border: 0, borderRadius: 12 }}
          loading="lazy"
        />
      ) : null}

      {mediaObject.mediaType == ExampleMediaType.SPOTIFY ? (
        <iframe
          width="100%"
          height={isSpotifyTrackLink(mediaObject.mediaLink || "") ? 353 : 393}
          src={formatSpotifyLink(mediaObject.mediaLink || "")}
          style={{ border: 0, borderRadius: 12 }}
          loading="lazy"
        />
      ) : null}
      {mediaObject.mediaType == ExampleMediaType.AUDIO ? (
        <div key={index} className={audioPlayerContainer}>
          {mediaObject.mediaTitle && <p className={audioPlayerTitle}>{mediaObject.mediaTitle}</p>}
          <div className={audioFlexContainer}>
            {mediaObject.mediaThumbnailImage && (
              <div className={audioImageContainer}>
                <ImageWithBlur
                  src={mediaObject.mediaThumbnailImage}
                  width={72}
                  height={72}
                  objectFit="cover"
                  className={audioImageBorderRadius}
                />
              </div>
            )}
            {mediaObject.mediaLink && (
              <AudioPlayer
                src={mediaObject.mediaLink}
                customStyles={{ width: "460px", waveContainerWidth: "295px" }}
              />
            )}
          </div>
        </div>
      ) : null}

      {isInEditMode && (
        <div className={styles.extraButtonsContainer}>
          <div
            role="button"
            className={classNames(styles.deleteIcon, `media_${index}_tooltip`)}
            data-tooltip-content="Delete Item"
            onClick={() => {
              props.handleDeleteItem(index);
            }}
          >
            <TrashIcon />
          </div>
          <Tooltip place="top" anchorSelect={`.media_${index}_tooltip`} />
        </div>
      )}
    </div>
  );
};

export const ApplyKnowledge = ({
  productData,
  storefrontData,
  isInEditMode,
}: {
  productData: ProductData;
  storefrontData: StorefrontData;
  isInEditMode: boolean;
}): React.ReactElement => {
  const {
    saveProductDraftUpdate,
    setIsSavingChanges,
    setSavingStatusText,
    CHANGES_SAVING_TEXT,
    CHANGES_SAVED_TEXT,
    CHANGES_ERROR_TEXT,
  } = useCreatorEditing();

  const [isAddingLink, setIsAddingLink] = useState(false);
  const [mediaLinkInputText, setMediaLinkInputText] = useState("");
  const [mediaTypeAdding, setMediaTypeAdding] = useState<ExampleMediaType>(
    ExampleMediaType.YOUTUBE,
  );
  const [mediaInputText, setMediaInputText] = useState(
    "Enter Spotify song, playlist, or artist link",
  );
  const [exampleMedia, setExampleMedia] = useState<ExampleMedia[]>(
    productData.exampleMedia?.map((media, index) => {
      return { ...media, mediaId: v4() };
    }) || [],
  );

  useEffect(() => {
    const exampleMedia =
      productData.exampleMedia?.map((media, index) => {
        return { ...media, mediaId: v4() };
      }) || [];

    setExampleMedia(exampleMedia);
  }, [productData]);

  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [audioPreviewLink, setAudioPreviewLink] = useState("");

  const initialAudioData: AudioLocalData = {
    isLoading: false,
    name: "",
    duration: 0,
    progress: 0,
  };

  const [audioData, setAudioData] = useState<AudioLocalData>(initialAudioData);
  const { user } = useAuth();
  //console.log("the product info ", productData);

  const saveNewMediaExamples = (
    newMediaExamples: ExampleMedia[],
    throttleSave: boolean = false,
  ) => {
    setIsSavingChanges(true);
    setSavingStatusText(CHANGES_SAVING_TEXT);
    setExampleMedia(newMediaExamples);

    saveProductDraftUpdate(productData, throttleSave, "exampleMedia", newMediaExamples).then(
      (res: { status: ApiStatus }) => {

        if (res.status == ApiStatus.SUCCESS) {
          setSavingStatusText(CHANGES_SAVED_TEXT);
        } else {
          setSavingStatusText(CHANGES_ERROR_TEXT);
        }
        setIsSavingChanges(false);
      },
    );
  };

  const handleDeleteMediaItem = (index: number) => {

    const newExampleMedia = [...exampleMedia];
    newExampleMedia.splice(index, 1);
    saveNewMediaExamples(newExampleMedia, false);
  };

  const onSubmit: SubmitHandler<{
    mediaLink: string;
  }> = async (data: { mediaLink: string }) => {


    let embeddableLink = mediaLinkInputText;
    let exampleMediaObject: ExampleMedia = {
      mediaId: v4(),
      mediaType: mediaTypeAdding,
      mediaLink: embeddableLink,
    };

    if (mediaTypeAdding == ExampleMediaType.AUDIO) {
      let audioTitleText = mediaLinkInputText;
      exampleMediaObject = {
        mediaId: v4(),
        mediaType: ExampleMediaType.AUDIO,
        mediaLink: audioPreviewLink,
        mediaTitle: audioTitleText,
      };
      if (storefrontData.welcomePosterImageURL) {
        exampleMediaObject.mediaThumbnailImage = storefrontData.welcomePosterImageURL;
      }
    }

    const newExampleMedia = [...exampleMedia, exampleMediaObject];
    saveNewMediaExamples(newExampleMedia);

    setMediaLinkInputText("");
    setAudioPreviewLink("");
    handleBackToMediaOptions();
  };

  const {
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
  } = useForm<{
    mediaLink: string;
  }>({
    defaultValues: {
      mediaLink: "",
    },
  });

  const handleAddMediaClick = (mediaType: ExampleMediaType) => {


    setMediaTypeAdding(mediaType);
    if (mediaType == ExampleMediaType.SPOTIFY) {
      setMediaInputText("Enter Spotify song, playlist, or artist link");
    }

    if (mediaType == ExampleMediaType.YOUTUBE) {
      setMediaInputText("Enter YouTube video link");
    }

    if (mediaType == ExampleMediaType.AUDIO) {
      setMediaInputText("Enter title for .mp3 file");
    }

    if (mediaType == ExampleMediaType.TIKTOK) {
      setMediaInputText("Enter TikTok video link");
    }

    setIsAddingLink(true);
  };

  const handleBackToMediaOptions = () => {
    setIsAddingLink(false);
    setMediaLinkInputText("");
  };

  const onUploadAudio = async (e: React.ChangeEvent<HTMLInputElement>, tag: CloudinaryTag) => {
    if (!e.target.files || !user?.userId) return;

    const file: File = e.target.files[0];

    const data: {
      file: File;
      userId: string;
      tag: CloudinaryTag;
    } = {
      file,
      userId: user?.userId,
      tag,
    };

    await uploadFileToCloudinary(data)
      .then((res) => {
        handleAddMediaClick(ExampleMediaType.AUDIO);
        setAudioPreviewLink(res.url || "");
      })
      .finally(() => {
        setIsAudioLoading(false);
      });
  };

  const onDeleteAudio = (type: CloudinaryTag): void => {
    // TODO: Delete media example
    setAudioData(initialAudioData);
  };

  const mediaOptions = (
    <div className={styles.newExampleMediaContainer}>
      {!isAudioLoading ? (
        <>
          <div
            className={styles.newExampleMediaOptionContainer}
            onClick={() => handleAddMediaClick(ExampleMediaType.SPOTIFY)}
          >
            <SpotifyIcon width={56} height={56} className={styles.mediaIcon} />
            <div>Embed Spotify link</div>
          </div>
          <label htmlFor="audio-input">
            <div className={styles.newExampleMediaOptionContainer}>
              <MusicNoteIcon className={styles.mediaIcon} />
              <div>Embed .mp3 file</div>
            </div>
          </label>
          <input
            type="file"
            className={styles.hiddenInput}
            id="audio-input"
            accept=".mp3"
            disabled={isAudioLoading}
            onChange={(e) => {
              setIsAudioLoading(true);
              onUploadAudio(e, CloudinaryTag.ClassMediaExampleAudio);
            }}
          />

          <div
            className={styles.newExampleMediaOptionContainer}
            onClick={() => handleAddMediaClick(ExampleMediaType.YOUTUBE)}
          >
            <YouTubeIcon width={58} height={58} className={styles.mediaIcon} />
            <div>Embed YouTube video</div>
          </div>
          <div
            className={styles.newExampleMediaOptionContainer}
            onClick={() => handleAddMediaClick(ExampleMediaType.TIKTOK)}
          >
            <TikTokIcon className={styles.mediaIcon} />
            <div>Embed TikTok video</div>
          </div>
        </>
      ) : (
        <>
          <AnimatedSpinner />
          {/* <p className={styles.audioPlaceholderText}>Uploading</p> */}
        </>
      )}
    </div>
  );

  const addLinkComponent = (
    <div className={styles.newExampleMediaContainer}>
      <div style={{ display: "flex", flexDirection: "row" }}>
        <div onClick={handleBackToMediaOptions} className={styles.backButton}>
          <div className={styles.backArrowContainer}>
            <ArrowRightIcon />
          </div>
          Back
        </div>
        <div className={styles.newExampleMediaContent}>
          <div className={styles.relativeContainer}>
            <LargeInput
              type="text"
              className={styles.mediaLinkInput}
              placeholder={mediaInputText}
              errorMessage={errors.mediaLink?.message}
              autoComplete="email"
              onChange={(e) => {
                setMediaLinkInputText(e.target.value);
              }}
              value={mediaLinkInputText}
            />
            <Button
              className={styles.mediaSubmitButton}
              onClick={handleSubmit(onSubmit)}
              isLoading={isSubmitting}
            >
              {mediaTypeAdding == ExampleMediaType.AUDIO ? "Embed" : "Embed"}
            </Button>
          </div>
          <AudioExampleMediaPlayer
            mediaObject={{
              mediaLink: audioPreviewLink,
              mediaId: v4(),
              mediaType: ExampleMediaType.AUDIO,
            }}
            index={0}
          />
        </div>
      </div>
    </div>
  );

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { delay: 150, tolerance: 100 } }),
    useSensor(PointerSensor, { activationConstraint: { delay: 150, tolerance: 100 } }),
  );

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (over !== null && active.id !== over.id) {
        const oldIndex = exampleMedia.findIndex((x: ExampleMedia) => x.mediaId === active.id);
        const newIndex = exampleMedia.findIndex((x: ExampleMedia) => x.mediaId === over.id);
        let tempMediaList = arrayMove(exampleMedia, oldIndex, newIndex);
        setExampleMedia(tempMediaList);
        saveNewMediaExamples(tempMediaList);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [exampleMedia],
  );

  return (
    <div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={exampleMedia.map((x: ExampleMedia) => {
            return x.mediaId;
          })}
          strategy={verticalListSortingStrategy}
        >
          {exampleMedia.map((mediaObject, index) => (
            <SortableMediaItem
              key={index}
              id={mediaObject.mediaId}
              index={index}
              isInEditMode={isInEditMode}
              mediaObject={{
                ...mediaObject,
                mediaThumbnailImage: storefrontData.welcomePosterImageURL,
              }}
              handleDeleteItem={handleDeleteMediaItem}
            />
          ))}
        </SortableContext>
      </DndContext>
      {isInEditMode && <div>{isAddingLink ? addLinkComponent : mediaOptions}</div>}
    </div>
  );
};
